import { useApp, useBehavior, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, ActionTypeEnum, AssetsSvgMap, TInputData, TWAction, TWidgetProps } from '@libs/types';
import { camelCaseToKebabCase, getFieldName } from '@libs/utils';
import React, { useMemo, useCallback, useState } from 'react';
import { CoreInputViewNew, CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { TInputOption } from '../../types/options';
import { ConsumerActionPlayerNameEnums } from '../../types/enums';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import { useConsumerInput } from '../../hooks/useInputs';

const getInputMode = (options: TInputOption): 'text' | 'numeric' | 'decimal' | 'email' => {
    const inputModeMap: { [key: string]: 'text' | 'numeric' | 'decimal' | 'email' } = {
        number: 'decimal',
        decimal: 'decimal',
        numericPassword: 'numeric',
        email: 'email',
    };

    return inputModeMap[options?.inputType ?? 'text'] || 'text';
};
const getInputType = (options: TInputOption): 'text' | 'number' | 'password' | 'decimal' => {
    const inputTypeMap: { [key: string]: 'text' | 'number' | 'password' | 'decimal' } = {
        number: 'number',
        decimal: 'number',
        password: 'password',
        numericPassword: 'password',
    };

    return inputTypeMap[options?.inputType ?? 'text'] || 'text';
};

type TTextInput = {
    inputRef?: React.RefObject<HTMLInputElement | null>;
    autoComplete?: string;
} & TWidgetProps<TInputData, TInputOption>;

const findPlayerOnClickAction = (actions: TWAction[] | undefined, name: ConsumerActionPlayerNameEnums): TWAction | undefined =>
    actions?.find((a) => a?.type === ActionTypeEnum.Player && a?.actionType === ActionActionTypeEnum.onClick && a?.name === name);

const StrokedInput = ({ widget, inputRef, autoComplete = 'off' }: TTextInput) => {
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const name = getFieldName(options.autofill);
    const [initialIsError] = useState(() => !!options.isError);
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerAction, triggerActionsByType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const { value, isFocused, isFilled, isError, errorMessage, label, helperText, handleChange, handleBlur, handleFocus, failedValidationInternalId } =
        useConsumerInput(widget, { isHidden, isDisabled, triggerActionsByType, setModal });
    const isPassword = options.inputType === 'numericPassword' || options.inputType === 'password';

    const inputAutoComplete = camelCaseToKebabCase(isPassword ? 'off' : (options.autofill ?? autoComplete));

    const helperAction = useMemo(() => findPlayerOnClickAction(widget.actions, ConsumerActionPlayerNameEnums.showHelperDescription), [widget.actions]);
    const errorHelperAction = useMemo(
        () => findPlayerOnClickAction(widget.actions, ConsumerActionPlayerNameEnums.showErrorHelperDescription),
        [widget.actions],
    );

    const isShowGreyHintButton = !isError && !!helperAction?.widgetId;
    const isShowErrorHintFromValidation = !!isError && !!failedValidationInternalId;
    const isShowErrorHintFromServer = initialIsError && !!errorHelperAction?.widgetId;
    const isShowErrorHintButton = failedValidationInternalId === undefined ? isShowErrorHintFromServer : isShowErrorHintFromValidation;
    const handleHintClick = helperAction ? () => triggerAction(helperAction) : undefined;

    const handleErrorClick = useCallback(() => {
        if (isShowErrorHintFromValidation && failedValidationInternalId) {
            const action: TWAction = {
                widgetId: failedValidationInternalId,
                type: ActionTypeEnum.Player,
                actionType: ActionActionTypeEnum.onClick,
                name: ConsumerActionPlayerNameEnums.showErrorHelperDescription,
                page: errorHelperAction?.page ?? '',
            };
            void triggerAction(action);
        } else if (isShowErrorHintFromServer && errorHelperAction) {
            void triggerAction(errorHelperAction);
        }
    }, [isShowErrorHintFromValidation, isShowErrorHintFromServer, failedValidationInternalId, errorHelperAction, triggerAction]);

    // This function temporarily sets the input field to readonly to prevent the password manager from triggering.
    // When the input field is focused, we remove the readonly attribute.
    const handleFocusWithReadOnly = (e: React.FocusEvent<HTMLInputElement>) => {
        if (handleFocus) {
            handleFocus(e);
        }
    };

    const parentStyleNames = [options.style].filter(Boolean) as string[];
    const displayedHelperText = isError && errorMessage ? errorMessage : helperText;
    const showHintIcon = isShowGreyHintButton || isShowErrorHintButton;

    return (
        <CoreInputViewNew
            uid={widget.uid}
            value={value || ''}
            label={label}
            isError={isError}
            isFocused={isFocused}
            isFilled={isFilled}
            isDisabled={isDisabled}
            isHide={isHidden}
            parentStyleNames={parentStyleNames}
            styleProps={{
                font: TextStyle.main.font,
                fontStyle: { ...TextStyle.main.fontStyle },
                main: 'var(--color-purple_100)',
                outline: 'var(--color-grey_16_lc)',
                margin: options.margin,
                hovered: { outline: 'var(--color-grey_24_lc)' },
                error: { onSurfaceVariant: 'var(--color-pink_100)' },
                focused: { outline: 'var(--color-grey_16_lc)', onSurfaceVariant: 'var(--color-purple_100)' },
                disabled: { onSurface: 'var(--color-grey_40_lc)', onSurfaceVariant: 'var(--color-grey_40_lc)' },
                label: { ...TextStyle.secondary },
            }}
            style={{ width: '100%' }}
            inputRef={inputRef}
            autoComplete={inputAutoComplete}
            name={name}
            autoFocus={options.onFocus || false}
            readOnly={isDisabled}
            type={getInputType(options)}
            inputMode={getInputMode(options)}
            maxLength={data.submitLength || options.maxLength}
            required={data.mandatory}
            tabIndex={data.tabIndex}
            onChange={(e) => handleChange?.(e)}
            onBlur={handleBlur}
            onFocus={handleFocusWithReadOnly}
        >
            {displayedHelperText && (
                <CoreLayoutView
                    uid={widget.uid + '_helperRow'}
                    isHide={isHidden}
                    isDisabled={isDisabled}
                    styleName="strokedInputHelperRow"
                    parentStyleNames={parentStyleNames}
                    styleProps={{
                        orientation: ORIENTATION_ENUM.HORIZONTAL,
                        lineY: 'center',
                        gap: 2,
                        padding: { top: 8, left: 16, right: 16 },
                    }}
                >
                    <CoreTextView
                        uid={widget.uid + '_helperText'}
                        text={displayedHelperText}
                        as="span"
                        isError={isError}
                        isDisabled={isDisabled}
                        styleName="strokedInputHelper"
                        parentStyleNames={parentStyleNames}
                        styleProps={{
                            ...TextStyle.secondary,
                            main: 'var(--color-grey_72_lc)',
                            error: { main: 'var(--color-pink_100)' },
                            disabled: { main: 'var(--color-grey_40_lc)' },
                        }}
                    />
                    {showHintIcon && (
                        <VisualView
                            uid={widget.uid + '_helperIcon'}
                            parentStyleNames={[...parentStyleNames, 'strokedInputHelperIcon']}
                            iconName={AssetsSvgMap.ic_info_circle_small}
                            iconVariant="simple"
                            iconHierarchy={isShowErrorHintButton ? 'destructive' : 'secondary'}
                            iconWidth={16}
                            iconHeight={16}
                            handleClick={isShowErrorHintButton ? handleErrorClick : handleHintClick}
                        />
                    )}
                </CoreLayoutView>
            )}
        </CoreInputViewNew>
    );
};

export { StrokedInput };
